import { Component, inject, OnInit, signal } from '@angular/core';
import { arabicNameValidators, optionalName } from '../../../../validators/name-rules';
import { FormGroup, FormControl, Validators, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { CategoryService } from '../../../../services/category/category.service';
import { CategoryDTO } from '../../../../pages/admin-dashboard/categories/category.model';
import { finalize } from 'rxjs';
import { CategoryFileMgt } from '../../../../services/category/fileMgt.category.service';

@Component({
  selector: 'app-add-category-form',
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './add-category-form.html',
  styleUrl: './add-category-form.scss',
})
export class AddCategoryForm implements OnInit {
  private categoryService = inject(CategoryService);
  private categoryFileMgt = inject(CategoryFileMgt);
  private router = inject(Router);

  loading = signal<boolean>(false);
  errorCreatingCategory = signal<string | null>(null);

  selectedImage: File | null = null;
  imagePreview: string | null = null;
  formSubmitted = false;
  isDragging = false;

  categoryForm = new FormGroup({
    titleEn: new FormControl('', [Validators.minLength(2), Validators.maxLength(100)]),
    titleAr: new FormControl('', arabicNameValidators(100)),
    descriptionEn: new FormControl('', [Validators.maxLength(4000)]),
    descriptionAr: new FormControl('', [Validators.maxLength(4000)]),
    profileImageFid: new FormControl(''),
  });

  ngOnInit() {}

  onFileSelect(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    const file = input.files[0];
    this.processFile(file);

    input.value = '';
  }

  processFile(file: File) {
    if (!file.type.match(/image\/(png|jpe?g)/)) {
      alert($localize`:@@categoryFormFileTypeError:Only PNG and JPG files are allowed`);
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert($localize`:@@categoryFormFileSizeError:File ${file.name} exceeds 5MB limit`);
      return;
    }

    this.selectedImage = file;

    const reader = new FileReader();
    reader.onload = (e) => {
      this.imagePreview = e.target?.result as string;
    };
    reader.readAsDataURL(file);
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.processFile(files[0]);
    }
  }

  removeImage() {
    this.selectedImage = null;
    this.imagePreview = null;
  }

  onSubmit() {
    this.formSubmitted = true;

    if (!this.selectedImage) {
      this.errorCreatingCategory.set('Please upload a category logo');
      return;
    }

    if (this.categoryForm.invalid) {
      for (const key in this.categoryForm.controls) {
        this.categoryForm.get(key)?.markAsTouched();
      }
      return;
    }

    this.loading.set(true);
    this.errorCreatingCategory.set(null);

    this.categoryFileMgt.uploadImage(this.selectedImage).subscribe({
      next: (result) => {
        const formValue = this.categoryForm.getRawValue();
        const payload: CategoryDTO = {
          titleEn: optionalName(formValue.titleEn),
          titleAr: formValue.titleAr!.trim(),
          descriptionEn: formValue.descriptionEn || undefined,
          descriptionAr: formValue.descriptionAr || undefined,
          profileImageFid: result.fid,
        };

        this.categoryService.createCategory(payload).subscribe({
          next: () => {
            this.loading.set(false);
            this.router.navigate(['/admin/categories']);
          },
          error: (err) => {
            console.error('Error creating category:', err);
            this.loading.set(false);
            this.errorCreatingCategory.set('Failed to create category. Please try again.');
          },
        });
      },
      error: (error) => {
        console.error('Error uploading image:', error);
        this.loading.set(false);
        this.errorCreatingCategory.set('Failed to upload image. Please try again.');
      },
    });
  }

  onCancel() {
    this.router.navigate(['/admin/categories']);
  }
}
